// =============================================================================
// API Response Types - Mirror Backend DTOs
// =============================================================================
// These types match the backend DTO structure from 04-03-SUMMARY.md
// All Decimal values are strings (backend serializes Decimal as string for JSON)

export type AssetClass = 'CRYPTO' | 'TRADFI'
export type ScenarioMode = 'HISTORICAL_REPLAY' | 'BTC_CURVE_REGIME' | 'CUSTOM_SHOCKS'
export type ReplayMode = 'PEAK_TO_TROUGH' | 'FULL_PATH'
export type BetaMode = 'NORMAL' | 'STRESSED'

// =============================================================================
// Historical Episode Types
// =============================================================================

export interface HistoricalEpisode {
  id: string
  name: string
  description: string
  referencePeriod: string
  startDate: string
  endDate: string
}

// =============================================================================
// Asset Types
// =============================================================================

export interface Asset {
  id: string
  symbol: string
  name: string
  assetClass: AssetClass
  currentPrice: string // Decimal as string
  quantity: string // Decimal as string
}

export interface AssetClassification {
  assetId: string
  symbol: string
  classification: AssetClass
  isOverride: boolean
}

// =============================================================================
// Portfolio Types
// =============================================================================

export interface Position {
  assetId: string
  symbol: string
  quantity: string // Decimal as string
  value: string // Decimal as string
}

export interface Portfolio {
  id: string
  name: string
  totalValue: string // Decimal as string
  positions: Position[]
  assetClassBreakdown: {
    CRYPTO: string // Decimal as string
    TRADFI: string // Decimal as string
  }
}

// =============================================================================
// Adjustment Scenario Types
// =============================================================================

export interface PositionChange {
  assetId: string
  symbol: string
  newQuantity: string // Decimal as string
}

export interface AdjustmentScenario {
  id: string
  name: string
  description?: string
  changes: PositionChange[]
}

export interface CreateAdjustmentRequest {
  name: string
  description?: string
  changes: PositionChange[]
}

export interface UpdateAdjustmentRequest {
  name?: string
  description?: string
  changes?: PositionChange[]
}

// =============================================================================
// Scenario Configuration Types
// =============================================================================

export interface HistoricalReplayConfig {
  episodeId: string
  replayType: 'PEAK_TO_TROUGH' | 'FULL_PATH'
}

export interface BTCCurveRegimeConfig {
  btcPricePath: number[]
  regimeTemplateId: string
  betaMode: BetaMode
}

export interface CustomShocksConfig {
  assetShocks: Record<string, number>
}

export type ModeConfig = HistoricalReplayConfig | BTCCurveRegimeConfig | CustomShocksConfig

// =============================================================================
// Simulation Request/Response Types
// =============================================================================

export interface SimulateScenarioRequest {
  portfolioId: string
  mode: ScenarioMode
  historicalConfig?: HistoricalReplayConfig
  btcRegimeConfig?: BTCCurveRegimeConfig
  customShocksConfig?: CustomShocksConfig
  adjustment_scenario_ids?: string[]
}

export interface ScenarioSummary {
  scenario_id: string
  mode: ScenarioMode
  mode_config: ModeConfig
  asset_classifications: AssetClassification[]
  computation_timestamp: string
  cache_hit: boolean
  execution_time_ms: number
}

// =============================================================================
// P&L Waterfall Types
// =============================================================================

export interface PortfolioPnLSummary {
  absolutePnL: string // Decimal as string
  percentagePnL: string // Decimal as string
  startingNAV: string // Decimal as string
  endingNAV: string // Decimal as string
}

export interface PositionPnL {
  assetId: string
  symbol: string
  absolutePnL: string // Decimal as string
  percentagePnL: string // Decimal as string
}

export interface AssetClassBucket {
  assetClass: AssetClass
  absolutePnL: string // Decimal as string
  percentagePnL: string // Decimal as string
  percentageOfPortfolio: string // Decimal as string
  positions: PositionPnL[]
}

export interface PnLWaterfall {
  portfolioTotal: PortfolioPnLSummary
  buckets: AssetClassBucket[]
}

// =============================================================================
// Adjustment Delta Types
// =============================================================================

export interface AdjustmentDeltas {
  comparison: string
  delta_absolute: string // Decimal as string
  delta_percentage: string // Decimal as string
}

// =============================================================================
// Risk Decomposition Types
// =============================================================================

export interface MarginalContribution {
  assetId: string
  symbol: string
  contribution: string // Decimal as string
}

export interface ConcentrationMetrics {
  hhi: string // Decimal as string (Herfindahl-Hirschman Index)
  top3_concentration: string // Decimal as string
}

export interface FactorExposure {
  factor: string
  exposure: string // Decimal as string
  beta: string // Decimal as string
}

export interface PortfolioConcentration {
  crypto_percentage: string // Decimal as string
  tradfi_percentage: string // Decimal as string
}

export interface RiskDecomposition {
  marginal_contributions: MarginalContribution[]
  concentration_metrics: ConcentrationMetrics
  factor_exposures: FactorExposure[]
  portfolio_concentration: PortfolioConcentration
}

// =============================================================================
// Drawdown Path Types
// =============================================================================

export interface DrawdownPoint {
  date: string
  nav: string // Decimal as string
  drawdown: string // Decimal as string
}

export interface DrawdownPath {
  points: DrawdownPoint[]
  max_drawdown: string // Decimal as string
  max_drawdown_date: string
}

// =============================================================================
// Coherence Diagnostic Types
// =============================================================================

export type CoherenceStatus = 'success' | 'insufficient_data' | 'computation_error'

export interface NearestHistoricalAnalog {
  episode_name: string
  similarity_score: number
}

export interface CoherenceDiagnostic {
  coherence_score: number | null
  mahalanobis_distance: number | null
  flag_text?: string
  calculation_status: CoherenceStatus
  nearest_historical_analog?: NearestHistoricalAnalog
}

// =============================================================================
// Proxy Audit Types
// =============================================================================

export interface ProxyAuditEntry {
  assetId: string
  symbol: string
  proxy_asset: string
  proxy_symbol: string
  splice_date: string
  blend_weight: string // Decimal as string
  rationale: string
}

// =============================================================================
// Complete 7-Key Response Type
// =============================================================================

export interface ScenarioResultResponse {
  scenario_summary: ScenarioSummary
  pnl_waterfall: PnLWaterfall
  adjustment_deltas?: AdjustmentDeltas
  risk_decomposition: RiskDecomposition
  drawdown_path: DrawdownPath | null
  coherence_diagnostic: CoherenceDiagnostic
  proxy_audit: ProxyAuditEntry[]
}

// =============================================================================
// Regime Template Types
// =============================================================================

export interface TradFiAssumptions {
  equityReturn: string
  ratesChange: string
  creditSpread: string
}

export interface RegimeTemplateListItemDto {
  id: string
  name: string
  description: string
  referencePeriod: string
  tradFiAssumptions: TradFiAssumptions
}

// =============================================================================
// API Error Types
// =============================================================================

export interface ApiErrorResponse {
  message: string
  code?: string
  details?: Record<string, string[]>
}

export class ApiError extends Error {
  status: number
  code?: string
  details?: Record<string, string[]>

  constructor(
    message: string,
    status: number,
    code?: string,
    details?: Record<string, string[]>
  ) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.details = details
  }
}
